/**
 * 이 탭이 쓰는 토큰. 요청 헤더에 실리는 것은 언제나 이 값이다.
 */
const TOKEN_KEY = 'access_token'

/**
 * 토큰 저장 — **sessionStorage 하나. 탭 단위이고 탭을 닫으면 사라진다.**
 *
 * 한때 localStorage 씨앗을 얹어 새 탭이 물려받게 했다(북마크로 열면 랜딩 없이 바로
 * 들어가게 하려고). 리뷰 다섯 바퀴 끝에 되돌렸다 — 이 구조는 "토큰은 탭 단위이고
 * 휘발성" 이라는 불변식을 뒤집는 것이어서, 그 위에서 쓰인 모든 경로를 다시 감사해야
 * 했고 마지막 라운드까지 **새로운 종류의 결함**이 나왔다. 마지막에 나온 것이 제일
 * 무거웠다: `__root.tsx` 가 URL 의 `?token=` 을 검증 없이 저장하는데, 씨앗이 있으면
 * 그 값이 브라우저 전역에 24시간 박혀 새로 여는 모든 탭이 남의 신원을 물려받는다
 * (세션 픽세이션의 폭발 반경이 탭 하나에서 브라우저 전체로 커진다).
 *
 * 다시 시도한다면 순서가 중요하다: **씨앗은 `/v2/auth/me` 검증을 통과한 토큰에만**
 * 심어야 하고, 요청에 실리는 값은 지금처럼 탭 사본이어야 하며(전역 저장소를 그대로
 * 읽으면 어드민이 새 탭에서 다른 고객을 여는 순간 열려 있던 탭의 데이터가 바뀐다),
 * 빌려온 세션(`?access_token=`·`/auth/link`)은 씨앗과 완전히 격리해야 한다.
 * 자세한 경위는 REQ-51 커밋 이력에 있다.
 */
export const auth = {
  // URL에서 토큰 추출 (OAuth 콜백 후)
  extractTokenFromUrl: (): string | null => {
    const params = new URLSearchParams(window.location.search)
    return params.get('token')
  },

  // 토큰 저장 (sessionStorage - 탭별 독립)
  //
  // **저장소 예외를 삼키지 않는다.** 한때 try/catch 로 감쌌다가 되돌렸다 — 저장이
  // 실패해도 호출부는 로그인이 성공한 줄 알고 진행하고, 그다음 모든 요청이 토큰 없이
  // 나가 401 로 튕긴다. 조용한 실패가 큰 실패보다 나쁘다. 예외는
  // `_dashboard.tsx` 의 바깥 catch 가 받아 "초기화 중 오류" 로 끊는다.
  // (감싸려면 이 모듈만이 아니라 요청 인터셉터까지 함께여야 한다 — 그쪽이
  //  매 요청 저장소를 직접 읽으므로, 여기만 감싸면 가드가 있는 줄 알게 될 뿐이다.)
  setToken: (token: string) => {
    sessionStorage.setItem(TOKEN_KEY, token)
  },

  // 토큰 가져오기
  getToken: (): string | null => {
    return sessionStorage.getItem(TOKEN_KEY)
  },

  // 토큰만 비운다 (이동 없음) — 화면을 그 자리에 두고 미인증 상태로만 되돌릴 때.
  clearToken: () => {
    sessionStorage.removeItem(TOKEN_KEY)
  },

  // 로그아웃 — 토큰을 비우고 진입점으로 돌아간다.
  logout: () => {
    auth.clearToken()
    window.location.href = '/'
  },

  // 인증 상태 확인
  isAuthenticated: (): boolean => {
    return !!auth.getToken()
  },
}

// 세션 만료 처리가 이미 시작됐는지 — 여러 요청이 동시에 401 을 받아도
// 리다이렉트를 한 번만 걸기 위한 가드 (탭 전체에서 1회)
let sessionExpiredHandled = false

// 인증 초기화가 몇 겹 돌고 있는지. 이 구간의 401 은 "세션이 끊겼다" 가 아니라
// "애초에 로그인 상태가 아니다" 라서, 만료 안내 리다이렉트를 걸면 안 된다.
//
// **불리언이 아니라 카운터인 이유**: 초기화가 겹쳐 돌 수 있다. React StrictMode 는
// 개발에서 이펙트를 두 번 실행하는데, 불리언이면 먼저 끝난 쪽의 정리가 표시를 꺼버려
// 아직 날아오는 다른 401 이 만료 처리로 새 나간다. 실제로 그 경로로 딥링크를 잃었다.
let authBootstrapDepth = 0

/**
 * 인증 초기화 구간 표시 (켠 쪽이 반드시 끈다 — `finally` 또는 이펙트 cleanup 으로 짝을 맞춘다).
 *
 * 이게 없으면 만료된 세션으로 딥링크를 열었을 때 검증 401 이 만료 처리를 부르고,
 * 보던 경로 대신 루트로 떨어진다 — 복귀 지점을 적을 기회조차 사라진다.
 *
 * 켜는 곳은 둘이다:
 *  - `_dashboard.tsx` 의 `initializeAuth` (세 분기 각각 try/finally)
 *  - `install-not-eligible.tsx` — 레이아웃 밖이라 자기 손으로 켠다. 죽은 토큰의 401 이
 *    안내 화면을 `/?session_expired=true` 로 날려버리는 것을 막는 용도.
 */
export function setAuthBootstrapping(value: boolean) {
  authBootstrapDepth = value
    ? authBootstrapDepth + 1
    : Math.max(0, authBootstrapDepth - 1)
}

/**
 * 고객 대시보드 세션 만료 처리 — 토큰을 지우고 안내와 함께 진입점으로 돌려보낸다.
 *
 * 두 HTTP 클라이언트가 공유한다:
 * - axios (`lib/api.ts` 응답 인터셉터)
 * - orval fetch mutator (`api/custom-instance.ts`)
 *
 * 대시보드 API 호출이 전부 orval 훅으로 옮겨간 뒤로 axios 쪽 인터셉터만으로는
 * 만료를 감지하지 못해, 세션이 끊겨도 화면이 살아있는 것처럼 보이는 구간이 있었다.
 *
 * `?session_expired=true` 는 `_dashboard.tsx` 가 읽어 토스트로 안내한다.
 */
export function handleSessionExpired() {
  // 초기화 중 401 은 미인증이다. 호출부가 토큰을 정리하고 랜딩을 그린다.
  if (authBootstrapDepth > 0) return
  if (sessionExpiredHandled) return
  sessionExpiredHandled = true
  auth.clearToken()

  // **경로를 버리고 루트로 보낸다.** 보던 화면을 지킨 채 떨어뜨리면 복귀가 가능해지긴
  // 하는데, 한 번 시도했다가 되돌렸다 — 지금 URL 을 그대로 들고 가면 Cafe24 콘솔로
  // 들어온 사람이 **무한 리로드에 갇힌다.** 콘솔 진입 URL 은 `/?mall_id=&timestamp=&hmac=`
  // 이고 아래 `_dashboard` 는 그 파라미터를 지우지 않는다. 2시간이 지난 timestamp 는
  // 백엔드가 401 로 거절하고(`auth/service.py:_validate_timestamp`), 그 401 을 다시
  // 이 함수가 받아 같은 URL 로 보낸다. 세션 JWT 는 7일짜리라 만료 시점은 사실상 항상
  // 2시간 이후다.
  //
  // 만료 이후의 복귀가 필요하면 "어느 경로에서 랜딩이 그려지는가" 를 문자열이 아니라
  // 라우터에게 물어야 한다. 그건 별도 작업이다.
  window.location.href = '/?session_expired=true'
}

/**
 * 백엔드가 401 과 함께 내려주는 "Cafe24 재연동 필요" 신호.
 *
 * `dependencies.py:get_current_shop` 이 refresh_token 폐기(invalid_grant) 상태일 때만
 * 이 코드로 던진다. 단순 토큰 만료(`cafe24_token_expired`)와 구분해야 하는 이유는
 * 해법이 다르기 때문이다 — 만료는 서버가 알아서 갱신하지만, 폐기는 고객이 직접
 * 재연동하기 전까지 주문 데이터가 계속 유실된다.
 */
export const CAFE24_REAUTH_REQUIRED = 'cafe24_reauth_required'

/**
 * 401 응답 본문에서 재연동 신호를 꺼낸다. 해당 없으면 null.
 *
 * shopNo 가 없으면 **1로 떨어뜨리지 않고 null** 로 둔다. 멀티샵 몰에서 기본값이 걸리면
 * shop_no=2 고객을 shop 1 로 재연동시키는 잘못된 복구가 되는데, 그건 실패보다 나쁘다
 * (고객은 성공한 줄 알고 떠난다). 안내 페이지가 shopNo 없으면 원클릭 CTA 대신
 * "카페24 관리자에서 앱 열기" 폴백을 보여준다.
 */
export function parseCafe24ReauthDetail(
  body: unknown,
): { mallId: string; shopNo: number | null } | null {
  const detail = (body as { detail?: unknown } | null | undefined)?.detail
  if (!detail || typeof detail !== 'object') return null

  const { code, mall_id, shop_no } = detail as Record<string, unknown>
  if (code !== CAFE24_REAUTH_REQUIRED || typeof mall_id !== 'string')
    return null

  return {
    mallId: mall_id,
    shopNo: typeof shop_no === 'number' ? shop_no : null,
  }
}

/**
 * Cafe24 연동 끊김 처리 — 재연동 안내 페이지로 보낸다.
 *
 * 세션 만료와 같은 가드를 공유한다: 한 화면에서 여러 요청이 동시에 401 을 받아도
 * 리다이렉트는 한 번만 걸려야 한다.
 *
 * 토큰을 지우는 이유: 남겨두면 안내 페이지에서 뒤로가기 했을 때 다시 대시보드가
 * 그려지려다 또 401 로 튕기는 왕복이 생긴다. 어차피 재연동하면 새로 발급된다.
 */
export function handleCafe24ReauthRequired(
  mallId: string,
  shopNo: number | null,
) {
  if (sessionExpiredHandled) return
  sessionExpiredHandled = true
  auth.clearToken()
  const params = new URLSearchParams({ mall_id: mallId })
  // shop_no 를 모르면 아예 안 넘긴다 — 추측한 값으로 엉뚱한 샵을 재연동시키지 않는다.
  if (shopNo !== null) params.set('shop_no', String(shopNo))
  window.location.href = `/cafe24-reauth?${params.toString()}`
}
